import { Flex } from '@chakra-ui/react';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { useAppDispatch, useAppSelector, useAppStore } from 'modules/Editor/store/hooks';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { RootState } from 'modules/Editor/store/store';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { memo, useMemo } from 'react';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { MultiEditChangeOption, MultiEditOption } from '../../../Widget.types';
import { useMultiEditConfig } from '../../../store/MultiEditConfigProvider';
import { CUSTOM_WIDGET_TOOLBAR_MAP } from './CustomMultiWidgetToolbar.config';
import { getAllMultiEditWidgetIds, getToolbarOptions } from './MultiWidgetToolbar.helpers';
import {
  MultiBold,
  MultiBorder,
  MultiBorderStyle,
  MultiBorderWidth,
  MultiColor,
  MultiItalic,
  MultiTextAlign,
  MultiTextColor,
  MultiUnderline,
  MultiWidgetFontFamily,
  MultiWidgetFontSize,
} from './MultiWidgetToolbarOptions';
import { MultiWidgetValueProvider } from './MultiWidgetValueProvider';

export const MultiWidgetToolbar = () => {
  const { boundingBox } = useBoundingBox();
  const dispatch = useAppDispatch();
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const store = useAppStore();
  const { getCustomUpdateFunction, getCustomKey } = useMultiEditConfig();
  const toolbarOptions = getToolbarOptions(activeWidgets);

  const isAllSameType = useMemo(() => {
    if (!activeWidgets?.length) return false;

    const firstWidgetType = getWidgetTypeFromId(activeWidgets[0].id);
    return activeWidgets.every((widget) => getWidgetTypeFromId(widget.id) === firstWidgetType);
  }, [activeWidgets]);

  // call functions / custom functions for updating the widget
  // The update has to be one transaction
  const onBatchUpdate = (option: MultiEditOption, value: any, updateFnName: MultiEditChangeOption) => {
    const widgets = (store.getState() as RootState).infograph.widgets;
    const allIds = getAllMultiEditWidgetIds(activeWidgets);

    const updateArray = allIds.map((widgetId) => {
      const customKey = getCustomKey(widgetId, option);

      // map a widgets custom key the key
      const keyName = customKey || option;
      const widgetData = widgets[widgetId];

      // if the widget has a custom function for updating the widget data -> call it
      // else return widgetData { [key]: value } e.g. widgetData: { color: 'red' }
      const customUpdateFunction = getCustomUpdateFunction(widgetId, updateFnName, option);
      const newWidgetData = customUpdateFunction ? customUpdateFunction({ value, widgetData }) : { [keyName]: value };

      return { widgetId, widgetData: newWidgetData };
    });

    if (!!updateArray?.length) dispatch(updateWidget(updateArray));

    // update the bounding box in case the widget dimensions update
    setTimeout(() => boundingBox.updateRect(), 0);
  };

  // Custom toolbar component to use when all widgets are of the same type - if not defined, will use default multi toolbar options
  const CustomToolbarComponent = CUSTOM_WIDGET_TOOLBAR_MAP[getWidgetTypeFromId(activeWidgets[0].id)];
  return (
    <MultiWidgetValueProvider>
      {isAllSameType && CustomToolbarComponent && <CustomToolbarComponent />}
      {!(isAllSameType && CustomToolbarComponent) && (
        <MultiToolbarOptions options={toolbarOptions} onBatchUpdate={onBatchUpdate} />
      )}
    </MultiWidgetValueProvider>
  );
};

type OnBatchUpdate = (option: MultiEditOption, value: any, updateFnName: MultiEditChangeOption) => void;

const MultiToolbarOptions = memo(
  ({ options, onBatchUpdate }: { options: MultiEditOption[]; onBatchUpdate: OnBatchUpdate }) => {
    return (
      <Flex gap='8px' data-testid='multi-widget-edit-toolbar'>
        {options.map((toolbarOption) => {
          if (toolbarOption === MultiEditOption.Color) {
            return <MultiColor key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.TextColor) {
            return <MultiTextColor key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.FontFamily) {
            return <MultiWidgetFontFamily key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.FontSize) {
            return <MultiWidgetFontSize key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.FontWeight) {
            return <MultiBold key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.FontStyle) {
            return <MultiItalic key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.TextDecoration) {
            return <MultiUnderline key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.TextAlign) {
            return <MultiTextAlign key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.BorderColor) {
            return <MultiBorder key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.BorderStyle) {
            return <MultiBorderStyle key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          if (toolbarOption === MultiEditOption.BorderWidth) {
            return <MultiBorderWidth key={toolbarOption} onBatchUpdate={onBatchUpdate} />;
          }

          return null;
        })}
      </Flex>
    );
  },
);
MultiToolbarOptions.displayName = 'MultiToolbarOptions';
